import { IExtractedCertificateOfInsurance, ICoverage } from "@/api";
import { getPropertyDisplayValue } from "@/utils/fields";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Stack,
    Typography,
} from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useCallback } from "react";

const FieldProperty = ({
    name,
    value,
}: {
    name: string;
    value: string | boolean | undefined;
}) => {
    return (
        <Stack
            direction="row"
            gap={1}
            sx={{
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
            }}
        >
            <Typography variant="body2">{name}</Typography>

            <Stack direction="row" gap={1.5} sx={{ alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                    {getPropertyDisplayValue(value)}
                </Typography>
            </Stack>
        </Stack>
    );
};

/** Shared accordion summary styling for inner accordions */
const innerAccordionSummarySx = {
    minHeight: 44,
    px: 1.5,
    transition: "background 0.15s ease",
    "&:hover": {
        bgcolor: "rgba(34, 56, 118, 0.02)",
        "& .MuiSvgIcon-root": {
            color: "#64748b",
        },
    },
    "&.Mui-expanded": {
        minHeight: 44,
    },
    "& .MuiAccordionSummary-content": {
        my: 1,
        "&.Mui-expanded": {
            my: 1,
        },
    },
};

const CoverageComponent = ({ coverage }: { coverage: ICoverage }) => {
    return (
        <Accordion disableGutters elevation={0}>
            <AccordionSummary
                expandIcon={
                    <ExpandMoreIcon
                        sx={{
                            color: "#94a3b8",
                            fontSize: 16,
                            transition: "color 0.15s ease",
                        }}
                    />
                }
                sx={innerAccordionSummarySx}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ width: "100%", pr: 0.5 }}
                >
                    <Typography
                        sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            color: "#1e293b",
                        }}
                    >
                        {coverage.type}
                    </Typography>
                    {coverage.insurer_letter && (
                        <Chip
                            label={`Insurer ${coverage.insurer_letter}`}
                            size="small"
                            color="tertiary"
                        />
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, py: 1.5 }}>
                <Stack spacing={2}>
                    {/* Policy Information */}
                    {(coverage.policy_number ||
                        coverage.policy_effective_date ||
                        coverage.policy_expiration_date) && (
                        <Box>
                            <Typography
                                variant="caption"
                                fontWeight="medium"
                                color="text.secondary"
                                sx={{ mb: 1, display: "block" }}
                            >
                                Policy Information
                            </Typography>
                            <Stack spacing={1}>
                                {coverage.policy_number && (
                                    <FieldProperty
                                        name="Policy Number"
                                        value={coverage.policy_number}
                                    />
                                )}
                                {coverage.policy_effective_date && (
                                    <FieldProperty
                                        name="Policy Effective Date"
                                        value={coverage.policy_effective_date}
                                    />
                                )}
                                {coverage.policy_expiration_date && (
                                    <FieldProperty
                                        name="Policy Expiration Date"
                                        value={coverage.policy_expiration_date}
                                    />
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* Coverage Options */}
                    {coverage.coverage_options &&
                        coverage.coverage_options.length > 0 && (
                            <Box>
                                <Typography
                                    variant="caption"
                                    fontWeight="medium"
                                    color="text.secondary"
                                    sx={{ mb: 1, display: "block" }}
                                >
                                    Coverage Options
                                </Typography>
                                <Stack spacing={1}>
                                    {coverage.coverage_options.map(
                                        (option, optionIndex) => (
                                            <FieldProperty
                                                key={optionIndex}
                                                name={option.name}
                                                value={option.value}
                                            />
                                        )
                                    )}
                                </Stack>
                            </Box>
                        )}

                    {/* Additional Coverage Details */}
                    {(coverage.additional_insured !== undefined ||
                        coverage.subrogation_waived !== undefined) && (
                        <Box>
                            <Typography
                                variant="caption"
                                fontWeight="medium"
                                color="text.secondary"
                                sx={{ mb: 1, display: "block" }}
                            >
                                Coverage Details
                            </Typography>
                            <Stack spacing={1}>
                                {coverage.additional_insured !== undefined && (
                                    <FieldProperty
                                        name="Additional Insured"
                                        value={coverage.additional_insured}
                                    />
                                )}
                                {coverage.subrogation_waived !== undefined && (
                                    <FieldProperty
                                        name="Subrogation Waived"
                                        value={coverage.subrogation_waived}
                                    />
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* Limits */}
                    {coverage.limits && coverage.limits.length > 0 && (
                        <Box>
                            <Typography
                                variant="caption"
                                fontWeight="medium"
                                color="text.secondary"
                                sx={{ mb: 1, display: "block" }}
                            >
                                Limits
                            </Typography>
                            <Stack spacing={1}>
                                {coverage.limits.map((limit, limitIndex) => (
                                    <FieldProperty
                                        key={limitIndex}
                                        name={limit.name}
                                        value={limit.value?.toString()}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};

interface IProps {
    coi: IExtractedCertificateOfInsurance;
    pageNavigationPluginInstance: PageNavigationPlugin;
}

export const CertificateOfInsurance = ({
    coi,
    pageNavigationPluginInstance,
}: IProps) => {
    const getInsurerDisplay = (name?: string, naic?: string) => {
        if (!name) return "-";
        if (name.trim() === "-") return "-";

        return `${name} ${naic ? "- NAIC #: " + naic : ""}`;
    };

    const handleChipClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();

            if (!coi.page_num) return;

            pageNavigationPluginInstance?.jumpToPage(coi.page_num - 1);
        },
        [coi.page_num, pageNavigationPluginInstance]
    );

    return (
        <Accordion disableGutters elevation={0} defaultExpanded>
            <AccordionSummary
                expandIcon={
                    <ExpandMoreIcon
                        sx={{
                            color: "#94a3b8",
                            fontSize: 18,
                            transition: "color 0.15s ease",
                        }}
                    />
                }
                sx={{
                    minHeight: 52,
                    px: 2,
                    transition: "background 0.15s ease",
                    "&:hover": {
                        bgcolor: "rgba(34, 56, 118, 0.02)",
                        "& .MuiSvgIcon-root": {
                            color: "#64748b",
                        },
                    },
                    "&.Mui-expanded": {
                        minHeight: 52,
                    },
                    "& .MuiAccordionSummary-content": {
                        my: 1.5,
                        "&.Mui-expanded": {
                            my: 1.5,
                        },
                    },
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%", pr: 0.5 }}
                >
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.9375rem",
                                color: "#0f172a",
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Certificate of Liability Insurance
                        </Typography>
                        <Chip
                            label={`p. ${coi.page_num}`}
                            onClick={handleChipClick}
                            size="small"
                            color="tertiary"
                            sx={{ height: 20, fontSize: "0.75rem" }}
                        />
                    </Stack>
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                sx={{ bgcolor: "background.default", py: 1.5, px: 0 }}
            >
                <Box
                    sx={{
                        borderTop: 1,
                        borderBottom: 1,
                        borderColor: "divider",
                        overflow: "hidden",
                        bgcolor: "background.paper",
                        p: 2,
                    }}
                >
                    {/* Certificate Info */}
                    <Accordion disableGutters elevation={0} defaultExpanded>
                        <AccordionSummary
                            expandIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        color: "#94a3b8",
                                        fontSize: 16,
                                        transition: "color 0.15s ease",
                                    }}
                                />
                            }
                            sx={innerAccordionSummarySx}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: "0.875rem",
                                    color: "#1e293b",
                                }}
                            >
                                Certificate Information
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, py: 1.5 }}>
                            <Stack spacing={2}>
                                {/* Certificate Date */}
                                <FieldProperty
                                    name="Certificate Date"
                                    value={coi.certificate_date}
                                />

                                {/* Certificate Number */}
                                <FieldProperty
                                    name="Certificate Number"
                                    value={coi.certificate_number}
                                />

                                {/* Revision Number */}
                                <FieldProperty
                                    name="Revision Number"
                                    value={coi.revision_number}
                                />

                                {/* Authorized Representative */}
                                <FieldProperty
                                    name="Authorized Representative"
                                    value={coi.authorized_representative}
                                />
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Producer */}
                    <Accordion disableGutters elevation={0} defaultExpanded>
                        <AccordionSummary
                            expandIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        color: "#94a3b8",
                                        fontSize: 16,
                                        transition: "color 0.15s ease",
                                    }}
                                />
                            }
                            sx={innerAccordionSummarySx}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: "0.875rem",
                                    color: "#1e293b",
                                }}
                            >
                                Producer
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, py: 1.5 }}>
                            <Stack spacing={2}>
                                {/* Producer Name */}
                                <FieldProperty
                                    name="Name"
                                    value={coi.producer_name}
                                />

                                {/* Producer Address */}
                                <FieldProperty
                                    name="Address"
                                    value={coi.producer_address}
                                />

                                {/* Producer Contact Name */}
                                <FieldProperty
                                    name="Contact Name"
                                    value={coi.producer_contact_name}
                                />

                                {/* Producer Phone */}
                                <FieldProperty
                                    name="Phone"
                                    value={coi.producer_phone}
                                />

                                {/* Producer Fax */}
                                <FieldProperty
                                    name="Fax"
                                    value={coi.producer_fax}
                                />

                                {/* Producer Email */}
                                <FieldProperty
                                    name="Email Address"
                                    value={coi.producer_email}
                                />
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Insured */}
                    <Accordion disableGutters elevation={0} defaultExpanded>
                        <AccordionSummary
                            expandIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        color: "#94a3b8",
                                        fontSize: 16,
                                        transition: "color 0.15s ease",
                                    }}
                                />
                            }
                            sx={innerAccordionSummarySx}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: "0.875rem",
                                    color: "#1e293b",
                                }}
                            >
                                Insured
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, py: 1.5 }}>
                            <Stack spacing={2}>
                                {/* Insured Name */}
                                <FieldProperty
                                    name="Name"
                                    value={coi.insured_name}
                                />

                                {/* Insured Address */}
                                <FieldProperty
                                    name="Address"
                                    value={coi.insured_address}
                                />
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Insurer(s) Affording Coverage */}
                    <Accordion disableGutters elevation={0} defaultExpanded>
                        <AccordionSummary
                            expandIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        color: "#94a3b8",
                                        fontSize: 16,
                                        transition: "color 0.15s ease",
                                    }}
                                />
                            }
                            sx={innerAccordionSummarySx}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: "0.875rem",
                                    color: "#1e293b",
                                }}
                            >
                                Insurer(s) Affording Coverage
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, py: 1.5 }}>
                            <Stack spacing={2}>
                                {/* Insurer A */}
                                <FieldProperty
                                    name="Insurer A"
                                    value={getInsurerDisplay(
                                        coi.insurer_a_name,
                                        coi.insurer_a_naic
                                    )}
                                />

                                {/* Insurer B */}
                                <FieldProperty
                                    name="Insurer B"
                                    value={getInsurerDisplay(
                                        coi.insurer_b_name,
                                        coi.insurer_b_naic
                                    )}
                                />

                                {/* Insurer C */}
                                <FieldProperty
                                    name="Insurer C"
                                    value={getInsurerDisplay(
                                        coi.insurer_c_name,
                                        coi.insurer_c_naic
                                    )}
                                />

                                {/* Insurer D */}
                                <FieldProperty
                                    name="Insurer D"
                                    value={getInsurerDisplay(
                                        coi.insurer_d_name,
                                        coi.insurer_d_naic
                                    )}
                                />

                                {/* Insurer E */}
                                <FieldProperty
                                    name="Insurer E"
                                    value={getInsurerDisplay(
                                        coi.insurer_e_name,
                                        coi.insurer_e_naic
                                    )}
                                />

                                {/* Insurer F */}
                                <FieldProperty
                                    name="Insurer F"
                                    value={getInsurerDisplay(
                                        coi.insurer_f_name,
                                        coi.insurer_f_naic
                                    )}
                                />
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Certificate Holder */}
                    <Accordion disableGutters elevation={0} defaultExpanded>
                        <AccordionSummary
                            expandIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        color: "#94a3b8",
                                        fontSize: 16,
                                        transition: "color 0.15s ease",
                                    }}
                                />
                            }
                            sx={innerAccordionSummarySx}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: "0.875rem",
                                    color: "#1e293b",
                                }}
                            >
                                Certificate Holder
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, py: 1.5 }}>
                            <Stack spacing={2}>
                                {/* Certificate Holder Name */}
                                <FieldProperty
                                    name="Name"
                                    value={coi.certificate_holder_name}
                                />

                                {/* Certificate Holder Address */}
                                <FieldProperty
                                    name="Address"
                                    value={coi.certificate_holder_address}
                                />
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Coverages */}
                    <Accordion disableGutters elevation={0} defaultExpanded>
                        <AccordionSummary
                            expandIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        color: "#94a3b8",
                                        fontSize: 16,
                                        transition: "color 0.15s ease",
                                    }}
                                />
                            }
                            sx={innerAccordionSummarySx}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ width: "100%", pr: 0.5 }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: "0.875rem",
                                        color: "#1e293b",
                                    }}
                                >
                                    Coverages
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minWidth: 20,
                                        height: 20,
                                        borderRadius: "50%",
                                        bgcolor: "#e2e8f0",
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: "0.7rem",
                                            fontWeight: 500,
                                            color: "#64748b",
                                        }}
                                    >
                                        {coi.coverages?.length || 0}
                                    </Typography>
                                </Box>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, py: 1.5 }}>
                            <Stack spacing={1}>
                                {coi.coverages && coi.coverages.length > 0 ? (
                                    coi.coverages.map((coverage, index) => (
                                        <CoverageComponent
                                            key={index}
                                            coverage={coverage}
                                        />
                                    ))
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontStyle: "italic" }}
                                    >
                                        No coverages found
                                    </Typography>
                                )}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Description of Operations */}
                    {coi.description_of_operations && (
                        <Accordion disableGutters elevation={0}>
                            <AccordionSummary
                                expandIcon={
                                    <ExpandMoreIcon
                                        sx={{
                                            color: "#94a3b8",
                                            fontSize: 16,
                                            transition: "color 0.15s ease",
                                        }}
                                    />
                                }
                                sx={innerAccordionSummarySx}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: "0.875rem",
                                        color: "#1e293b",
                                    }}
                                >
                                    Description of Operations
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 1.5, py: 1.5 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {coi.description_of_operations}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};
